"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, SunMedium, Wrench, Clock, Box } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const partnershipFeatures = [{
  title: "Gutter-clearance handrails",
  description: "Configured to leave guttering and roof tiles fully accessible so your brackets and array fixings fit without scaffold interference.",
  spec: "Zero overhang obstruction"
}, {
  title: "Unobstructed eaves lifts",
  description: "Working platforms built precisely below the eaves line to give installers comfortable working height across the entire lower array.",
  spec: "Optimised working heights"
}, {
  title: "Dedicated material hoist towers",
  description: "Integrated lifting bays and gin wheels designed specifically for hoisting solar PV panels and ballast safely up to roof level.",
  spec: "Tested lifting zones"
}, {
  title: "Synchronised strike schedules",
  description: "Reliable dismantle scheduling matched directly to your commissioning timelines, preventing idle scaffolding on client driveways.",
  spec: "Fast turnaround"
}];
const accreditationBadges = [{
  name: "NASC Full Member",
  detail: "Audited safety standard"
}, {
  name: "CHAS Accredited",
  detail: "Contractor safety certified"
}];
function SolarPartnerContent() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.45,
    ease: [0.16, 1, 0.3, 1]
  };
  const photoEntry = posts.find((p: any) => p.customFields?.photo_name === 'Solar Installation Image' || p.acf?.photo_name === 'Solar Installation Image' || p.title === 'Solar Installation Image');
  const imgSrc = photoEntry?.customFields?.image || (photoEntry as any)?.acf?.image || photoEntry?.featuredImage || 'https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffolding_installation.png';
  const altText = photoEntry?.customFields?.alt_text || (photoEntry as any)?.acf?.alt_text || 'Solar panel installation taking place on a safety scaffold platform';
  const isCustom = Boolean(imgSrc && imgSrc !== 'https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffolding_installation.png');
  return <section id="solar-installer-partnership" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 border-b border-border overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Header Block with Datum Rule */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-md bg-accent text-accent-foreground text-xs font-bold tracking-wider uppercase">
            <SunMedium className="size-3.5 text-primary" />
            <span>Solar PV Sector Partner</span>
          </div>
          <div className="w-12 h-1 bg-primary mb-6 rounded-full" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5">
            Scaffolding built around your solar installation workflow
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            We partner directly with commercial and domestic solar PV contractors across the South East. Every scaffold is planned to keep your roof teams safe, speed up panel loading, and strike promptly once commissioning wraps.
          </p>
        </div>

        {/* Main Grid: Features on Left, Visual Showcase on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Feature List Column */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {partnershipFeatures.map((item, i) => <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            ...transitionConfig,
            delay: shouldReduceMotion ? 0 : i * 0.08
          }}>
                <Card className="border border-border bg-card shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="size-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          {i === 0 && <Wrench className="size-4.5 text-primary" />}
                          {i === 1 && <Box className="size-4.5 text-primary" />}
                          {i === 2 && <ShieldCheck className="size-4.5 text-primary" />}
                          {i === 3 && <Clock className="size-4.5 text-primary" />}
                        </div>
                        <div>
                          <h3 className="text-base sm:text-lg font-bold text-card-foreground mb-1.5">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-normal">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <span className="hidden sm:inline-flex items-center shrink-0 px-2.5 py-1 rounded-sm bg-muted text-muted-foreground text-xs font-medium">
                        {item.spec}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          {/* Media & Support Column */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            ...transitionConfig,
            delay: shouldReduceMotion ? 0 : 0.25
          }} className="relative rounded-lg overflow-hidden border border-border bg-card shadow-md">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={imgSrc} data-wvc-srcset={isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffolding_installation.png-wvc-srcset"} data-wvc-sizes={isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffolding_installation.png-wvc-sizes"} alt={altText} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
              </div>

              {/* In-Image Operational Details Card */}
              <div className="p-6 bg-card border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <div className="size-2 rounded-full bg-primary" />
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">
                    Installer Operational Standard
                  </p>
                </div>
                <h4 className="text-base font-bold text-card-foreground mb-2">
                  TG20:21 compliant compliance certificates on every handover
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed mb-5">
                  Handover packs, scaffolding tags, and weekly inspection logs supplied digitally for your principal contractor compliance folders.
                </p>

                {/* Accreditations Strip */}
                <div className="grid grid-cols-2 gap-2 pt-4 border-t border-border">
                  {accreditationBadges.map((badge, i) => <div key={badge.name} data-index={i} className="rounded-sm bg-muted/60 p-2 text-center">
                      <div className="flex items-center justify-center gap-1 mb-0.5">
                        <CheckCircle2 className="size-3 text-primary" />
                        <span className="text-[11px] font-bold text-foreground truncate">
                          {badge.name.split(" ")[0]}
                        </span>
                      </div>
                      <span className="text-[10px] text-muted-foreground block truncate">
                        {badge.detail}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Quick Action Box */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            ...transitionConfig,
            delay: shouldReduceMotion ? 0 : 0.35
          }} className="p-6 rounded-lg bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-secondary">
              <div>
                <h4 className="text-sm font-bold text-secondary-foreground mb-1">
                  Planning a residential or commercial solar rollout?
                </h4>
                <p className="text-xs text-muted-foreground">
                  Send your site plan or schedule for fixed-rate installer pricing.
                </p>
              </div>
              <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shrink-0">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5">
                  <span>Get installer rates</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}
export default function SolarPartner() {
  const wp_query = new WP_Query({
    post_type: 'site_photo',
    per_page: 100,
    status: ['publish']
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <SolarPartnerContent />
    </PostsProvider>;
}